/**
 * Carrying a visitor's intent across the sign-in boundary.
 *
 * Most of the app never needs this: a gated route renders the login screen at
 * its own URL, and reloading after sign-in lands the visitor exactly where
 * they were. The public catalog is the exception — those pages render for
 * everyone, so pressing "Start learning" has to say where to come back to.
 */

/** Where a visitor goes after signing in with nothing else asked for. */
export const DEFAULT_DESTINATION = '/app'

/**
 * A `next` value is only usable if it points back into this site.
 *
 * Anything else is an open redirect: `?next=https://evil.example` would let a
 * link that looks like ours hand someone to a copy of our sign-in page. Only
 * a path is accepted — not a full URL, even our own — and `//host` is refused
 * because browsers read it as protocol-relative and leave the origin.
 *
 * @param {string | null | undefined} value
 * @returns {string | null}
 */
export function safeNext(value) {
  if (!value) return null
  if (!value.startsWith('/')) return null
  if (value.startsWith('//')) return null
  if (value.includes('\\')) return null
  return value
}

/** Read the validated `next` out of a query string. */
export function nextFrom(search = '') {
  return safeNext(new URLSearchParams(search).get('next'))
}

/**
 * A link to the sign-in screen that returns here afterwards.
 *
 * @param {string} [returnTo] path to come back to; defaults to the current URL
 * @param {'login' | 'signup'} [tab] which panel to open on
 */
export function signInHref(returnTo, tab) {
  const params = new URLSearchParams()
  if (tab === 'signup') params.set('tab', 'signup')
  const next = safeNext(returnTo)
  if (next) params.set('next', next)
  const query = params.toString()
  return query ? `/login?${query}` : '/login'
}

const OAUTH_NEXT_KEY = 'copr:auth:next'

/**
 * Hold the destination across a Google sign-in.
 *
 * The OAuth round trip leaves the site and comes back to /auth-callback, which
 * has no `?next=` of its own. Without this, a visitor who pressed "Unlock" on
 * a course from an ad finished signing in on the dashboard instead of checkout.
 * Session storage survives the round trip because it stays in the same tab.
 *
 * @param {string | null | undefined} next
 */
export function rememberOAuthNext(next) {
  try {
    const safe = safeNext(next)
    if (safe) sessionStorage.setItem(OAUTH_NEXT_KEY, safe)
    else sessionStorage.removeItem(OAUTH_NEXT_KEY)
  } catch { /* private mode: falls back to the default destination */ }
}

/** Read and forget the destination held by `rememberOAuthNext`. */
export function takeOAuthNext() {
  try {
    const next = sessionStorage.getItem(OAUTH_NEXT_KEY)
    sessionStorage.removeItem(OAUTH_NEXT_KEY)
    return safeNext(next)
  } catch {
    return null
  }
}

/**
 * The course or book a signed-out visitor is about to buy, if the sign-in
 * screen stands in front of a checkout: either rendered by the gated
 * /subscribe route itself (an ad linking straight to checkout) or reached with
 * `?next=/subscribe?…` from an Unlock button.
 *
 * @param {string} pathname
 * @param {string} search
 * @returns {{ content_type: 'course' | 'book', object_id: number } | null}
 */
export function purchaseIntentFrom(pathname, search) {
  let query = null
  if (pathname === '/subscribe') query = search
  else {
    const next = nextFrom(search)
    if (next && (next === '/subscribe' || next.startsWith('/subscribe?'))) query = next.slice('/subscribe'.length)
  }
  if (!query) return null
  const params = new URLSearchParams(query)
  const content_type = params.get('content_type')
  const object_id = Number(params.get('object_id'))
  if (content_type !== 'course' && content_type !== 'book') return null
  if (!Number.isInteger(object_id) || object_id <= 0) return null
  return { content_type, object_id }
}
